import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Divider,
  Link,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArticleIcon from '@mui/icons-material/Article';
import ImageIcon from '@mui/icons-material/Image';
import SettingsIcon from '@mui/icons-material/Settings';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PublishIcon from '@mui/icons-material/Publish';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import RichTextEditor from '../components/RichTextEditor';
import TwoFactorSetup from '../components/TwoFactorSetup';
import EquipmentAdminPanel from '../components/EquipmentAdminPanel';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import { getImageUrl } from '../utils/imageUrl';

function TabPanel({ children, value, tabValue }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== tabValue}
      id={`tabpanel-${tabValue}`}
      aria-labelledby={`tab-${tabValue}`}
    >
      {value === tabValue && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Admin = () => {
  // Image management state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Video management state
  const [videos, setVideos] = useState([]);
  const [newVideo, setNewVideo] = useState({ title: '', url: '', description: '' });
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    url: ''
  });
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  // News management state
  const [news, setNews] = useState([]);
  const [newsForm, setNewsForm] = useState({ title: '', content: '', image: null, imagePreview: null });
  const [isEditing, setIsEditing] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);

  // Site settings state
  const [settings, setSettings] = useState({ icon: '', spaceHeader: '' });

  // Credential management state
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI state
  const [activeTab, setActiveTab] = useState('gallery');
  const [editDialog, setEditDialog] = useState({
    open: false,
    imageId: null,
    title: '',
    description: '',
    telescope: '',
    mount: '',
    camera: '',
    filter: '',
    duration: '',
    url: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { logout } = useAuth();

  // Pagination state
  const [pagination, setPagination] = useState({
    gallery: { page: 0, rowsPerPage: 12 },
    news: { page: 0, rowsPerPage: 5 },
    videos: { page: 0, rowsPerPage: 8 }
  });

  // Image update state
  const [newImage, setNewImage] = useState(null);

  useEffect(() => {
    fetchImages();
    fetchVideos();
    fetchNews();
    fetchSettings();
    fetchCredentials();
  }, []);

  // Image management functions
  const getAuthHeaders = (extraHeaders = {}) => {
    const token = localStorage.getItem('token');
    return token
      ? { ...extraHeaders, 'Authorization': `Bearer ${token}` }
      : extraHeaders;
  };

  const handleImageUpload = async (file, title = '', description = '') => {
    try {
      console.log('Starting upload:', { file, title, description });
      
      const formData = new FormData();
      formData.append('image', file);
      formData.append('title', title);
      formData.append('description', description);

      console.log('Uploading to:', `${config.endpoints.gallery}/upload`);
      
      const response = await fetch(`${config.endpoints.gallery}/upload`, {
        method: 'POST',
        body: formData,
        headers: getAuthHeaders({
          'Accept': 'application/json',
        }),
      });

      console.log('Upload response status:', response.status);

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || 'Failed to upload image';
        } catch (e) {
          errorMessage = `Server returned ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Upload successful:', data);
      
      setImages(prev => [...prev, data]);
      setSnackbar({
        open: true,
        message: 'Image uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Upload error details:', error);
      setSnackbar({
        open: true,
        message: `Error uploading image: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await fetch(config.endpoints.gallery);
      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();
      
      // Transform the URLs to use HTTPS
      const imagesWithHttps = data.map(image => ({
        ...image,
        url: getImageUrl(image.url)
      }));
      
      setImages(imagesWithHttps);
    } catch (error) {
      console.error('Error fetching images:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch images',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: 'Please select an image first',
        severity: 'error'
      });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      const response = await fetch(config.endpoints.gallery, {
        method: 'POST',
        body: formData,
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to upload image');
      }

      const data = await response.json();
      
      // Add the new image to the state
      setImages(prevImages => [...prevImages, {
        ...data,
        url: getImageUrl(data.url) // Ensure URL is HTTPS
      }]);
      
      // Reset form
      setSelectedFile(null);
      document.getElementById('image-upload').value = '';
      
      setSnackbar({
        open: true,
        message: 'Image uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload image',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    setItemToDelete({ type: 'gallery', id: imageId });
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (image) => {
    setEditDialog({
      open: true,
      imageId: image.id,
      url: getImageUrl(image.url),
      title: image.title || '',
      description: image.description || '',
      telescope: image.telescope || '',
      mount: image.mount || '',
      camera: image.camera || '',
      filter: image.filter || '',
      duration: image.duration || ''
    });
  };

  const handleCloseEdit = () => {
    setEditDialog({
      open: false,
      imageId: null,
      title: '',
      description: '',
      telescope: '',
      mount: '',
      camera: '',
      filter: '',
      duration: '',
      url: ''
    });
    setNewImage(null);
  };

  const handleEditSubmit = async () => {
    try {
      // First update the image file if a new one was selected
      if (newImage) {
        const formData = new FormData();
        formData.append('image', newImage);
        
        const imageResponse = await fetch(`${config.endpoints.gallery}/${editDialog.imageId}/image`, {
          method: 'PUT',
          body: formData,
          headers: getAuthHeaders(),
        });
        
        if (!imageResponse.ok) {
          throw new Error('Failed to update image file');
        }
      }

      // Then update the image details
      const response = await fetch(`${config.endpoints.gallery}/${editDialog.imageId}`, {
        method: 'PUT',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          title: editDialog.title,
          description: editDialog.description,
          telescope: editDialog.telescope,
          mount: editDialog.mount,
          camera: editDialog.camera,
          filter: editDialog.filter,
          duration: editDialog.duration
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update image details');
      }

      // Refresh the gallery
      fetchImages();
      handleCloseEdit();
      setSnackbar({ open: true, message: 'Image updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Error updating image:', error);
      setSnackbar({ open: true, message: 'Failed to update image', severity: 'error' });
    }
  };

  const handleImageUpdate = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewImage(file);
    }
  };

  const handleDeleteClick = async (id) => {
    try {
      const response = await fetch(`${config.endpoints.gallery}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      // Remove the deleted image from the state
      setImages(images.filter(image => image.id !== id));
      setSnackbar({ open: true, message: 'Image deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error deleting image:', error);
      setSnackbar({ open: true, message: 'Failed to delete image', severity: 'error' });
    }
  };

  // Video management functions
  const fetchVideos = async () => {
    try {
      const response = await fetch(`${config.endpoints.videos}`);
      if (!response.ok) throw new Error('Failed to fetch videos');
      const data = await response.json();
      setVideos(data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch videos',
        severity: 'error'
      });
    }
  };

  const handleAddVideo = async () => {
    try {
      const response = await fetch(`${config.endpoints.videos}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newVideo),
      });

      if (!response.ok) throw new Error('Failed to add video');
      const data = await response.json();
      setVideos([...videos, data]);
      setNewVideo({ title: '', url: '', description: '' });
      setSnackbar({
        open: true,
        message: 'Video added successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to add video',
        severity: 'error'
      });
    }
  };

  const handleDeleteVideo = async (id) => {
    setItemToDelete({ type: 'videos', id });
    setDeleteDialogOpen(true);
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.title,
      description: video.description,
      url: video.url
    });
    setVideoDialogOpen(true);
  };

  const handleUpdateVideo = async () => {
    try {
      const response = await fetch(`${config.endpoints.videos}/${editingVideo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(videoForm),
      });

      if (!response.ok) throw new Error('Failed to update video');

      const updatedVideo = await response.json();
      setVideos(videos.map(video => 
        video.id === updatedVideo.id ? updatedVideo : video
      ));
      setVideoDialogOpen(false);
      setEditingVideo(null);
      setVideoForm({ title: '', description: '', url: '' });
    } catch (error) {
      console.error('Error updating video:', error);
    }
  };

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    // Handle different YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
      /youtube\.com\/watch\?.*v=([^&]+)/,
      /youtube\.com\/shorts\/([^&\?\/]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  // Get YouTube thumbnail URL
  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
  };

  // News management functions
  const fetchNews = async () => {
    try {
      const response = await fetch(`${config.endpoints.news}`);
      if (!response.ok) throw new Error('Failed to fetch news');
      const data = await response.json();
      setNews(data);
    } catch (error) {
      console.error('Error fetching news:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch news articles',
        severity: 'error'
      });
    }
  };

  const handleNewsImageSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${config.endpoints.upload}/news`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      
      // Update the news form with the image URL using getImageUrl
      setNewsForm(prev => ({
        ...prev,
        image: data.url,
        imagePreview: getImageUrl(data.url)
      }));

      setSnackbar({
        open: true,
        message: 'Image uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload image',
        severity: 'error'
      });
    }
  };

  const handleNewsSubmit = async (event) => {
    event.preventDefault();
    const legacyApiPort = ['510', '00'].join('');
    const legacyApiOrigins = [
      `https://avsiteapi.astrovinoserver.synology.me:${legacyApiPort}/api`,
      `https://api.astro-vino.co.uk:${legacyApiPort}/api`
    ];
    const updatedContent = legacyApiOrigins.reduce(
      (content, origin) => content.split(origin).join('/api'),
      newsForm.content
    );

    const formData = new FormData();
    formData.append('title', newsForm.title);
    formData.append('content', updatedContent);
    if (newsForm.image) {
      formData.append('image', newsForm.image);
    }

    try {
      const token = localStorage.getItem('token');
      const url = isEditing 
        ? `${config.endpoints.news}/${newsForm.id}`
        : `${config.endpoints.news}`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to save news article');
      }

      const updatedArticle = await response.json();
      
      if (isEditing) {
        setNews(prevNews => prevNews.map(article =>
          article.id === updatedArticle.id ? updatedArticle : article
        ));
        setSnackbar({
          open: true,
          message: 'Article updated successfully',
          severity: 'success'
        });
      } else {
        setNews(prevNews => [...prevNews, updatedArticle]);
        setSnackbar({
          open: true,
          message: 'Article published successfully',
          severity: 'success'
        });
      }

      setNewsForm({ title: '', content: '', image: null, imagePreview: null });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving news:', error);
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  };

  const handleEditClickNews = async (article) => {
    setIsEditing(true);
    setNewsForm({
      id: article.id,
      title: article.title,
      content: article.content || '',
      image: null,
      imagePreview: article.image ? getImageUrl(article.image) : null
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.endpoints.news}/${article.id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      });

      if (!response.ok) {
        throw new Error('Failed to fetch full article content');
      }

      const fullArticle = await response.json();
      setNewsForm({
        id: fullArticle.id,
        title: fullArticle.title,
        content: fullArticle.content || '',
        image: null,
        imagePreview: fullArticle.image ? getImageUrl(fullArticle.image) : (article.image ? getImageUrl(article.image) : null)
      });
    } catch (error) {
      console.error('Error loading full article for editing:', error);
      setSnackbar({
        open: true,
        message: 'Opened article from list data; could not refresh full content',
        severity: 'warning'
      });
    }
  };


  const handleCancelEditNews = () => {
    setNewsForm({
      id: null,
      title: '',
      content: '',
      image: null,
      imagePreview: null
    });
    setIsEditing(false);
  };

  const handleDeleteNews = async (id) => {
    setItemToDelete({ type: 'news', id });
    setDeleteDialogOpen(true);
  };

  const handleDeleteClickNews = (article) => {
    setItemToDelete({ type: 'news', id: article.id, title: article.title });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    const { type, id } = itemToDelete;
    try {
      const response = await fetch(`${config.endpoints[type]}/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete item');
      }

      // Update the state based on type
      if (type === 'news') {
        setNews(news.filter(item => item.id !== id));
      } else if (type === 'gallery') {
        setImages(images.filter(item => item.id !== id));
      } else if (type === 'videos') {
        setVideos(videos.filter(item => item.id !== id));
      }

      setSnackbar({
        open: true,
        message: 'Item deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting item:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete item',
        severity: 'error'
      });
    }

    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  // Site settings functions
  const fetchSettings = async () => {
    try {
      const response = await fetch(`${config.endpoints.settings}`);
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleIconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('icon', file);

      const response = await fetch(`${config.endpoints.settings}/icon`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload icon');
      }

      const updatedSettings = await response.json();
      setSettings(prevSettings => ({
        ...prevSettings,
        icon: updatedSettings.icon
      }));

      setSnackbar({
        open: true,
        message: 'Icon uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error uploading icon:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload icon',
        severity: 'error'
      });
    }
  };

  const handleHeaderUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('spaceHeader', file);

      const response = await fetch(`${config.endpoints.settings}/header`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload header');
      }

      const updatedSettings = await response.json();
      setSettings(prevSettings => ({
        ...prevSettings,
        spaceHeader: updatedSettings.spaceHeader
      }));

      setSnackbar({
        open: true,
        message: 'Header uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error uploading header:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload header',
        severity: 'error'
      });
    }
  };

  // Credential management functions
  const fetchCredentials = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.endpoints.credentials}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch credentials');
      const data = await response.json();
      setUsername(data.username);
    } catch (error) {
      console.error('Error fetching credentials:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch credentials',
        severity: 'error'
      });
    }
  };

  const handleCredentialsUpdate = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: 'New passwords do not match',
        severity: 'error'
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.endpoints.credentials}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username,
          currentPassword,
          newPassword: newPassword || undefined
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to update credentials');
      }

      setSnackbar({
        open: true,
        message: 'Credentials updated successfully',
        severity: 'success'
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // If username changed, logout to force re-login with new credentials
      const data = await response.json();
      if (data.username !== username) {
        logout();
      }
    } catch (error) {
      console.error('Error updating credentials:', error);
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Clean article text for display
  const cleanArticleText = (content) => {
    if (!content) return '';
    const div = document.createElement('div');
    div.innerHTML = content;
    
    // Remove HTML tags and get plain text
    const text = div.textContent || div.innerText || '';
    
    // Limit to a preview length and remove extra whitespace
    const maxLength = 200;
    const cleanText = text
      .replace(/\s+/g, ' ')
      .trim();
    
    return cleanText.length > maxLength 
      ? cleanText.substring(0, maxLength) + '...'
      : cleanText;
  };

  // Parse description
  const parseDescription = (description) => {
    try {
      return JSON.parse(description);
    } catch {
      return description;
    }
  };

  // Pagination component
  const PaginationControls = ({ section, totalItems }) => {
    const { page, rowsPerPage } = pagination[section];
    const totalPages = Math.ceil(totalItems / rowsPerPage);

    const handlePageChange = (newPage) => {
      setPagination(prev => ({
        ...prev,
        [section]: { ...prev[section], page: newPage }
      }));
    };

    const handleRowsPerPageChange = (event) => {
      const newRowsPerPage = Number(event.target.value);
      setPagination(prev => ({
        ...prev,
        [section]: { page: 0, rowsPerPage: newRowsPerPage }
      }));
    };

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            onClick={() => handlePageChange(Math.max(0, page - 1))}
            disabled={page === 0}
            variant="outlined"
          >
            Previous
          </Button>
          <Typography variant="body2">
            Page {page + 1} of {totalPages}
          </Typography>
          <Button
            onClick={() => handlePageChange(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            variant="outlined"
          >
            Next
          </Button>
          <Box sx={{ minWidth: 120, ml: 2 }}>
            <select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.23)',
                backgroundColor: '#1e1e1e',
                color: 'inherit',
                cursor: 'pointer',
                appearance: 'auto'
              }}
            >
              {section === 'gallery' ? (
                <>
                  <option value={12} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>12 per page</option>
                  <option value={24} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>24 per page</option>
                  <option value={48} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>48 per page</option>
                </>
              ) : section === 'videos' ? (
                <>
                  <option value={8} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>8 per page</option>
                  <option value={16} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>16 per page</option>
                  <option value={32} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>32 per page</option>
                </>
              ) : (
                <>
                  <option value={5} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>5 per page</option>
                  <option value={10} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>10 per page</option>
                  <option value={25} style={{ backgroundColor: '#1e1e1e', color: 'inherit' }}>25 per page</option>
                </>
              )}
            </select>
          </Box>
        </Box>
      </Box>
    );
  };

  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.23)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
      }
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiInputBase-input': {
      color: 'white',
    },
  };

  const renderNewsArticles = () => {
    const { page, rowsPerPage } = pagination.news;
    const paginatedArticles = news.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    return (
      <Grid container spacing={2}>
        {paginatedArticles.map((article) => (
          <Grid item xs={12} sm={6} md={4} key={article.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {article.image && (
                <CardMedia
                  component="img"
                  height="200"
                  image={getImageUrl(article.image)}
                  alt={article.title}
                  sx={{ objectFit: 'cover' }}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h6" component="h2">
                  {article.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(article.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton onClick={() => handleEditClickNews(article)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteClickNews(article)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderGalleryImages = () => {
    const { page, rowsPerPage } = pagination.gallery;
    const paginatedImages = images.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    return (
      <Grid container spacing={3}>
        {paginatedImages.map((image) => (
          <Grid item xs={12} sm={6} md={4} key={image.id}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={image.url} // URL is already HTTPS from fetchImages
                alt={image.title || 'Gallery image'}
                sx={{ objectFit: 'cover' }}
              />
              <CardContent>
                <Typography variant="subtitle1" component="div" noWrap>
                  {image.title || image.filename}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {image.description || ''}
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton 
                  onClick={() => handleEditClick(image)} 
                  sx={{ color: 'primary.light' }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  onClick={() => handleDeleteClick(image.id)}
                  sx={{ color: 'error.light' }}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderEditDialog = () => {
    return (
      <Dialog
        open={editDialog.open}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1e1e1e',
            color: 'white',
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          bgcolor: '#2d2d2d',
          px: 3,
          py: 2
        }}>
          Edit Image
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper 
                elevation={3}
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: '#2d2d2d',
                  borderRadius: '8px',
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <img 
                    src={editDialog.url}
                    alt={editDialog.title}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '300px', 
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }} 
                  />
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<PhotoCamera />}
                    sx={{ 
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      bgcolor: '#4a4a4a',
                      '&:hover': {
                        bgcolor: '#5a5a5a'
                      }
                    }}
                  >
                    Update Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpdate}
                    />
                  </Button>
                </Box>
                {newImage && (
                  <Typography variant="caption" display="block" sx={{ mt: 1.5, color: 'success.main' }}>
                    New image selected: {newImage.name}
                  </Typography>
                )}
              </Paper>
              <Paper 
                elevation={3}
                sx={{ 
                  bgcolor: '#2d2d2d',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                    Description
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <RichTextEditor
                    value={editDialog.description}
                    onChange={(content) => setEditDialog({ ...editDialog, description: content })}
                    style={{ height: '150px' }}
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={3}
                sx={{ 
                  p: 3,
                  bgcolor: '#2d2d2d',
                  borderRadius: '8px'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 500,
                  mb: 3,
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  pb: 1
                }}>
                  Technical Details
                </Typography>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={editDialog.title}
                    onChange={(e) => setEditDialog({ ...editDialog, title: e.target.value })}
                    variant="outlined"
                    sx={textFieldStyle}
                  />
                  <TextField
                    fullWidth
                    label="Telescope"
                    value={editDialog.telescope}
                    onChange={(e) => setEditDialog({ ...editDialog, telescope: e.target.value })}
                    variant="outlined"
                    sx={textFieldStyle}
                  />
                  <TextField
                    fullWidth
                    label="Mount"
                    value={editDialog.mount}
                    onChange={(e) => setEditDialog({ ...editDialog, mount: e.target.value })}
                    variant="outlined"
                    sx={textFieldStyle}
                  />
                  <TextField
                    fullWidth
                    label="Camera"
                    value={editDialog.camera}
                    onChange={(e) => setEditDialog({ ...editDialog, camera: e.target.value })}
                    variant="outlined"
                    sx={textFieldStyle}
                  />
                  <TextField
                    fullWidth
                    label="Filter"
                    value={editDialog.filter}
                    onChange={(e) => setEditDialog({ ...editDialog, filter: e.target.value })}
                    variant="outlined"
                    sx={textFieldStyle}
                  />
                  <TextField
                    fullWidth
                    label="Duration (in seconds)"
                    value={editDialog.duration}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setEditDialog({ ...editDialog, duration: value });
                    }}
                    type="number"
                    variant="outlined"
                    sx={textFieldStyle}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 1,
          bgcolor: '#2d2d2d',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Button
            onClick={handleCloseEdit}
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.23)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderSiteSettings = () => {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Site Settings
        </Typography>
        <TwoFactorSetup />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Site Icon</Typography>
                <Box sx={{ mb: 2 }}>
                  {settings.icon && (
                    <CardMedia
                      component="img"
                      height="100"
                      image={getImageUrl(settings.icon)}
                      alt="Site Icon"
                      sx={{ objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder.png';
                      }}
                    />
                  )}
                </Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="icon-upload"
                  type="file"
                  onChange={handleIconUpload}
                />
                <label htmlFor="icon-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                  >
                    {settings.icon ? 'Change Icon' : 'Upload Icon'}
                  </Button>
                </label>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Space Header</Typography>
                <Box sx={{ mb: 2 }}>
                  {settings.spaceHeader && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={getImageUrl(settings.spaceHeader)}
                      alt="Space Header"
                      sx={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder.png';
                      }}
                    />
                  )}
                </Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="header-upload"
                  type="file"
                  onChange={handleHeaderUpload}
                />
                <label htmlFor="header-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                  >
                    {settings.spaceHeader ? 'Change Header' : 'Upload Header'}
                  </Button>
                </label>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
          Admin Dashboard
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={logout}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            }
          }}
        >
          Logout
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
        
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab 
            icon={<ImageIcon />} 
            label="GALLERY" 
            value="gallery"
            sx={{ 
              minWidth: 120,
              '&.Mui-selected': { color: 'primary.main' }
            }}
          />
          <Tab 
            icon={<VideoLibraryIcon />} 
            label="VIDEOS" 
            value="videos"
            sx={{ 
              minWidth: 120,
              '&.Mui-selected': { color: 'primary.main' }
            }}
          />
          <Tab 
            icon={<ArticleIcon />} 
            label="NEWS" 
            value="news"
            sx={{ 
              minWidth: 120,
              '&.Mui-selected': { color: 'primary.main' }
            }}
          />
          <Tab 
            icon={<PrecisionManufacturingIcon />} 
            label="EQUIPMENT" 
            value="equipment"
            sx={{ 
              minWidth: 120,
              '&.Mui-selected': { color: 'primary.main' }
            }}
          />
          <Tab 
            icon={<SettingsIcon />} 
            label="SITE SETTINGS" 
            value="settings"
            sx={{ 
              minWidth: 120,
              '&.Mui-selected': { color: 'primary.main' }
            }}
          />
          <Tab 
            icon={<SettingsIcon />} 
            label="CREDENTIALS" 
            value="credentials"
            sx={{ 
              minWidth: 120,
              '&.Mui-selected': { color: 'primary.main' }
            }}
          />
        </Tabs>

        <TabPanel value={activeTab} tabValue="gallery">
          <Typography variant="h6" gutterBottom>Image Management</Typography>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Upload New Image
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
              >
                Select Image
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileSelect}
                />
              </Button>
              {selectedFile && (
                <Typography variant="body2" color="textSecondary">
                  Selected: {selectedFile.name}
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? <CircularProgress size={24} /> : 'Upload'}
              </Button>
            </Box>
          </Paper>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            Manage Images
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            renderGalleryImages()
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Showing {Math.min(pagination.gallery.rowsPerPage, images.length)} of {images.length} images
            </Typography>
          </Box>
          <PaginationControls section="gallery" totalItems={images.length} />
        </TabPanel>

        <TabPanel value={activeTab} tabValue="videos">
          <Typography variant="h6" gutterBottom>Video Management</Typography>
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Video Title"
                  value={newVideo.title}
                  onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Video URL"
                  value={newVideo.url}
                  onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newVideo.description}
                  onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleAddVideo}
                  startIcon={<VideoLibraryIcon />}
                >
                  Add Video
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Grid container spacing={2}>
            {videos
              .slice(
                pagination.videos.page * pagination.videos.rowsPerPage,
                pagination.videos.page * pagination.videos.rowsPerPage + pagination.videos.rowsPerPage
              )
              .map((video) => (
                <Grid item xs={12} md={6} key={video.id}>
                  <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {getYouTubeThumbnail(video.url) ? (
                      <CardMedia
                        component="img"
                        sx={{
                          height: 180,
                          objectFit: 'cover',
                          backgroundColor: 'rgba(0, 0, 0, 0.1)'
                        }}
                        image={getYouTubeThumbnail(video.url)}
                        alt={video.title}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <VideoLibraryIcon sx={{ fontSize: 60, opacity: 0.5 }} />
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {video.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {video.description}
                      </Typography>
                      <Link 
                        href={video.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Watch Video
                      </Link>
                    </CardContent>
                    <CardActions>
                      <IconButton
                        onClick={() => handleEditVideo(video)}
                        sx={{ color: 'primary.light' }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteVideo(video.id)}
                        sx={{ color: 'primary.light' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
          </Grid>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Showing {Math.min(pagination.videos.rowsPerPage, videos.length)} of {videos.length} videos
            </Typography>
          </Box>
          <PaginationControls section="videos" totalItems={videos.length} />
        </TabPanel>

        <TabPanel value={activeTab} tabValue="news">
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {isEditing ? 'Edit Article' : 'Create New Article'}
            </Typography>
            <Box component="form" onSubmit={handleNewsSubmit}>
              <TextField
                fullWidth
                label="Title"
                value={newsForm.title}
                onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                margin="normal"
                required
              />
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.82)' }}>
                  Article content
                </Typography>
                <RichTextEditor
                  key={newsForm.id || 'new-article'}
                  value={newsForm.content}
                  onChange={(content) => setNewsForm({ ...newsForm, content })}
                  minHeight={420}
                />
              </Box>
              <input
                type="file"
                accept="image/*"
                onChange={handleNewsImageSelect}
                style={{ display: 'none' }}
                id="news-image-input"
              />
              <label htmlFor="news-image-input">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<PhotoLibraryIcon />}
                  sx={{ mb: 2 }}
                >
                  {newsForm.imagePreview ? 'Change Image' : 'Upload Image'}
                </Button>
              </label>
              {newsForm.imagePreview && (
                <Box sx={{ mb: 2 }}>
                  <img
                    src={newsForm.imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '200px', maxHeight: '200px' }}
                  />
                </Box>
              )}
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                {isEditing ? (
                  <>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                    >
                      Update Article
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleCancelEditNews}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<PublishIcon />}
                  >
                    Publish Article
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Typography variant="h6" gutterBottom>
            Manage Articles
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Showing {Math.min(pagination.news.rowsPerPage, news.length)} of {news.length} articles
            </Typography>
          </Box>
          {renderNewsArticles()}
          <PaginationControls section="news" totalItems={news.length} />
        </TabPanel>

        <TabPanel value={activeTab} tabValue="equipment">
          <EquipmentAdminPanel setSnackbar={setSnackbar} />
        </TabPanel>

        <TabPanel value={activeTab} tabValue="settings">
          {renderSiteSettings()}
        </TabPanel>

        <TabPanel value={activeTab} tabValue="credentials">
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Admin Credentials
              </Typography>
              <Box component="form" onSubmit={handleCredentialsUpdate}>
                <TextField
                  fullWidth
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="New Password (leave blank to keep current)"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  error={newPassword !== confirmPassword}
                  helperText={newPassword !== confirmPassword ? "Passwords don't match" : ''}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                >
                  Update Credentials
                </Button>
              </Box>
            </Paper>
          </Grid>
        </TabPanel>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={editDialog.open}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1e1e1e',
            color: 'white',
            borderRadius: '12px',
          }
        }}
      >
        {editDialog.open && (
          <>
            <DialogTitle sx={{ 
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              bgcolor: '#2d2d2d',
              px: 3,
              py: 2
            }}>
              Edit Image
            </DialogTitle>
            <DialogContent sx={{ p: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Paper 
                    elevation={3}
                    sx={{ 
                      p: 2, 
                      mb: 3, 
                      bgcolor: '#2d2d2d',
                      borderRadius: '8px',
                      textAlign: 'center',
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <img 
                        src={editDialog.url}
                        alt={editDialog.title}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '300px', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }} 
                      />
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<PhotoCamera />}
                        sx={{ 
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          bgcolor: '#4a4a4a',
                          '&:hover': {
                            bgcolor: '#5a5a5a'
                          }
                        }}
                      >
                        Update Image
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageUpdate}
                        />
                      </Button>
                    </Box>
                    {newImage && (
                      <Typography variant="caption" display="block" sx={{ mt: 1.5, color: 'success.main' }}>
                        New image selected: {newImage.name}
                      </Typography>
                    )}
                  </Paper>
                  <Paper 
                    elevation={3}
                    sx={{ 
                      bgcolor: '#2d2d2d',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                        Description
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <RichTextEditor
                        value={editDialog.description}
                        onChange={(content) => setEditDialog({ ...editDialog, description: content })}
                        style={{ height: '150px' }}
                      />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={3}
                    sx={{ 
                      p: 3,
                      bgcolor: '#2d2d2d',
                      borderRadius: '8px'
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 500,
                      mb: 3,
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      pb: 1
                    }}>
                      Technical Details
                    </Typography>
                    <Stack spacing={2.5}>
                      <TextField
                        fullWidth
                        label="Title"
                        value={editDialog.title}
                        onChange={(e) => setEditDialog({ ...editDialog, title: e.target.value })}
                        variant="outlined"
                        sx={textFieldStyle}
                      />
                      <TextField
                        fullWidth
                        label="Telescope"
                        value={editDialog.telescope}
                        onChange={(e) => setEditDialog({ ...editDialog, telescope: e.target.value })}
                        variant="outlined"
                        sx={textFieldStyle}
                      />
                      <TextField
                        fullWidth
                        label="Mount"
                        value={editDialog.mount}
                        onChange={(e) => setEditDialog({ ...editDialog, mount: e.target.value })}
                        variant="outlined"
                        sx={textFieldStyle}
                      />
                      <TextField
                        fullWidth
                        label="Camera"
                        value={editDialog.camera}
                        onChange={(e) => setEditDialog({ ...editDialog, camera: e.target.value })}
                        variant="outlined"
                        sx={textFieldStyle}
                      />
                      <TextField
                        fullWidth
                        label="Filter"
                        value={editDialog.filter}
                        onChange={(e) => setEditDialog({ ...editDialog, filter: e.target.value })}
                        variant="outlined"
                        sx={textFieldStyle}
                      />
                      <TextField
                        fullWidth
                        label="Duration (in seconds)"
                        value={editDialog.duration}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setEditDialog({ ...editDialog, duration: value });
                        }}
                        type="number"
                        variant="outlined"
                        sx={textFieldStyle}
                      />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ 
              p: 3, 
              gap: 1,
              bgcolor: '#2d2d2d',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Button
                onClick={handleCloseEdit}
                variant="outlined"
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditSubmit}
                variant="contained"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingVideo ? 'Edit Video' : 'Add Video'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={videoForm.title}
              onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Video URL"
              value={videoForm.url}
              onChange={(e) => setVideoForm({ ...videoForm, url: e.target.value })}
              margin="normal"
              helperText="Enter YouTube video URL"
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Description
              </Typography>
              <RichTextEditor
                value={videoForm.description}
                onChange={(content) => setVideoForm({ ...videoForm, description: content })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setVideoDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            CANCEL
          </Button>
          <Button 
            onClick={handleUpdateVideo} 
            variant="contained" 
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.main',
                opacity: 0.9
              }
            }}
          >
            SAVE
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, bgcolor: 'rgba(0, 0, 0, 0.3)' }}>
          <Button
            onClick={handleDeleteCancel}
            variant="contained"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            CANCEL
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{ 
              bgcolor: '#f44336',
              color: 'white',
              '&:hover': {
                bgcolor: '#d32f2f'
              }
            }}
            autoFocus
          >
            DELETE
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Admin;
